// Email rendering: blocks in, a mobile-friendly HTML email and its plain-text twin out.
// Pure and dependency-free so the web app imports the same code for its live preview (@couragemfg/api/email-render):
// what the composer shows is what the recipient gets. Table layout and inline styles, because email clients.

export type Block =
  | { id: string; type: "heading"; text: string; size?: "large" | "medium" }
  | { id: string; type: "text"; text: string }
  | { id: string; type: "button"; label: string; url: string; align?: "left" | "center" }
  | { id: string; type: "image"; src: string; alt?: string; url?: string; width?: "full" | "half" }
  | { id: string; type: "quote"; text: string; by?: string }
  | { id: string; type: "divider" }
  | { id: string; type: "spacer"; size?: "s" | "m" | "l" }
  | { id: string; type: "signature" }
  /** A design made somewhere else (Claude, a designer, another tool), imported as HTML. It's the whole email. */
  | { id: string; type: "html"; html: string };
export type BlockType = Block["type"];
export type HtmlBlock = Extract<Block, { type: "html" }>;

/** The imported design, when the email is one. */
export const designOf = (blocks: Block[] | null | undefined): HtmlBlock | null => (blocks ?? []).find((b): b is HtmlBlock => b.type === "html") ?? null;
/** Imports bigger than this are refused; Gmail already cuts off anything over about 100 KB. */
export const MAX_DESIGN = 500_000;

export const BLOCK_TYPES: { type: BlockType; label: string; hint: string }[] = [
  { type: "heading", label: "Heading", hint: "A title for a section" },
  { type: "text", label: "Text", hint: "Paragraphs and lists. **bold**, *italic*, [a link](https://…)" },
  { type: "button", label: "Button", hint: "One clear action" },
  { type: "image", label: "Image", hint: "A photo or graphic from a web address" },
  { type: "quote", label: "Quote", hint: "What a customer said" },
  { type: "divider", label: "Divider", hint: "A line between sections" },
  { type: "spacer", label: "Space", hint: "Room to breathe" },
  { type: "signature", label: "Signature", hint: "The sender's name and email" },
];

/** What a merge tag can say about the person and the sender. */
export type Vars = {
  first_name?: string | null; last_name?: string | null; company?: string | null; job_title?: string | null; account?: string | null;
  email?: string | null; owner_name?: string | null; owner_email?: string | null; sender_name?: string | null; sender_email?: string | null;
};
export const MERGE_TAGS: { tag: keyof Vars | "full_name"; label: string; example: string }[] = [
  { tag: "first_name", label: "First name", example: "{{first_name|there}}" },
  { tag: "last_name", label: "Last name", example: "{{last_name}}" },
  { tag: "full_name", label: "Full name", example: "{{full_name}}" },
  { tag: "company", label: "Company", example: "{{company|your team}}" },
  { tag: "job_title", label: "Job title", example: "{{job_title}}" },
  { tag: "account", label: "Account", example: "{{account}}" },
  { tag: "owner_name", label: "Their account owner", example: "{{owner_name}}" },
  { tag: "owner_email", label: "Owner's email", example: "{{owner_email}}" },
  { tag: "sender_name", label: "Sender", example: "{{sender_name}}" },
];

export type Brand = { color?: string | null; logoUrl?: string | null; companyName?: string | null };
export type Footer = {
  postalAddress?: string | null; text?: string | null;
  /** Why they're getting it, e.g. "You're getting this because you're a customer of Acme." */ reason?: string | null;
  prefsUrl?: string | null; unsubscribeUrl?: string | null;
};
export type Utm = { campaign: string; content?: string | null } | null;
export type RenderInput = { subject: string; preheader?: string | null; blocks: Block[]; brand?: Brand; footer?: Footer; vars?: Vars; utm?: Utm };

export const esc = (s: unknown) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
const COLOR = /^#[0-9a-f]{6}$/i;

/** {{first_name|there}} → the value, or the fallback when it's empty. Unknown tags are left as they are.
 *  In HTML, pass esc so a name like "<b>Ann</b>" arrives as text; the fallback is the author's own. */
export function merge(template: string, vars: Vars = {}, escape: (s: string) => string = (s) => s): string {
  return String(template ?? "").replace(/\{\{\s*([a-z_]+)\s*(?:\|([^}]*))?\}\}/gi, (whole, key: string, fallback?: string) => {
    const k = key.toLowerCase();
    const v = k === "full_name" ? [vars.first_name, vars.last_name].filter(Boolean).join(" ") : (vars as Record<string, unknown>)[k];
    if (!(k in TAG_KEYS)) return whole;
    const s = typeof v === "string" ? v.trim() : "";
    return s ? escape(s) : (fallback ?? "").trim();
  });
}
const TAG_KEYS: Record<string, true> = Object.fromEntries([...MERGE_TAGS.map((t) => t.tag), "email", "sender_email"].map((k) => [k, true]));

/** Merge tags used in a template that aren't known, so the composer can point at a typo. */
export function unknownTags(...texts: string[]): string[] {
  const out = new Set<string>();
  for (const t of texts) for (const m of String(t ?? "").matchAll(/\{\{\s*([a-z_]+)\s*(?:\|[^}]*)?\}\}/gi)) if (!(m[1].toLowerCase() in TAG_KEYS)) out.add(m[1]);
  return [...out];
}

export const safeUrl = (u: string) => {
  const s = String(u ?? "").trim();
  return /^(https?:\/\/|mailto:|tel:)/i.test(s) ? s : s ? `https://${s.replace(/^\/+/, "")}` : "";
};
/** Tag a web link with utm_source, utm_medium and utm_campaign so website visits trace back to the email. */
export function tagLink(url: string, utm: Utm): string {
  const u = safeUrl(url);
  if (!utm || !/^https?:\/\//i.test(u) || /[?&]utm_/i.test(u)) return u;
  const [base, hash] = u.split("#", 2);
  const q = `utm_source=courage&utm_medium=email&utm_campaign=${encodeURIComponent(utm.campaign)}${utm.content ? `&utm_content=${encodeURIComponent(utm.content)}` : ""}`;
  return `${base}${base.includes("?") ? "&" : "?"}${q}${hash ? `#${hash}` : ""}`;
}

/** The little bit of formatting text blocks understand: **bold**, *italic*, [label](url) and bare https links. */
export function inline(s: string, link: (u: string) => string, color: string): string {
  let out = esc(s);
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label: string, url: string) => `<a href="${esc(link(url.replace(/&amp;/g, "&")))}" style="color:${color};text-decoration:underline">${label}</a>`);
  out = out.replace(/(^|[\s(])(https?:\/\/[^\s<)]+)/g, (_, pre: string, url: string) => `${pre}<a href="${esc(link(url.replace(/&amp;/g, "&")))}" style="color:${color};text-decoration:underline">${url}</a>`);
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>").replace(/(^|[^*])\*([^*\s][^*]*)\*/g, "$1<em>$2</em>");
  return out;
}
const plainInline = (s: string, link: (u: string) => string) =>
  s.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label: string, url: string) => `${label} (${link(url)})`).replace(/\*\*([^*]+)\*\*/g, "$1").replace(/(^|[^*])\*([^*\s][^*]*)\*/g, "$1$2");

/** Paragraphs split on a blank line; lines starting with "- " or "• " become a bulleted list. */
export function paragraphs(text: string): { kind: "p" | "ul"; lines: string[] }[] {
  const out: { kind: "p" | "ul"; lines: string[] }[] = [];
  for (const chunk of String(text ?? "").replace(/\r\n/g, "\n").split(/\n\s*\n/)) {
    const lines = chunk.split("\n").map((l) => l.trimEnd()).filter((l) => l.trim());
    if (!lines.length) continue;
    let cur: { kind: "p" | "ul"; lines: string[] } | null = null;
    for (const l of lines) {
      const bullet = /^\s*[-•*]\s+/.test(l);
      const kind = bullet ? "ul" : "p";
      if (!cur || cur.kind !== kind) { cur = { kind, lines: [] }; out.push(cur); }
      cur.lines.push(bullet ? l.replace(/^\s*[-•*]\s+/, "") : l);
    }
  }
  return out;
}

const FONT = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";
const INK = "#1B1F23", MUTED = "#5C6670";

export function renderEmail(input: RenderInput): { subject: string; html: string; text: string } {
  const vars = input.vars ?? {};
  const color = input.brand?.color && COLOR.test(input.brand.color) ? input.brand.color : "#0F6E6E";
  const link = (u: string) => tagLink(merge(u, vars), input.utm ?? null);
  const m = (s: string) => merge(s, vars);
  const subject = m(input.subject).replace(/[\r\n]+/g, " ").trim();
  const preheader = m(input.preheader ?? "");
  const html: string[] = [], text: string[] = [];
  const design = designOf(input.blocks);

  for (const b of design ? [] : input.blocks ?? []) {
    switch (b.type) {
      case "heading": {
        const t = m(b.text); if (!t.trim()) break;
        const size = b.size === "medium" ? 18 : 24;
        html.push(`<tr><td style="padding:8px 32px 4px;font-family:${FONT};font-size:${size}px;line-height:1.3;font-weight:700;color:${INK}">${esc(t)}</td></tr>`);
        text.push(t, "");
        break;
      }
      case "text": {
        // A person's values (a name typed into a public form) go in as plain text: the formatting is parsed with a
        // placeholder where each value goes, so "[click](https://…)" in a first name never becomes a link.
        const vals: string[] = [];
        const t = merge(b.text, vars, (v) => { vals.push(v.replace(/\s+/g, " ")); return `\u0000${vals.length - 1}\u0000`; }); if (!t.replace(/\u0000\d+\u0000/g, "x").trim()) break;
        const put = (s: string, f: (v: string) => string) => s.replace(/\u0000(\d+)\u0000/g, (_, i: string) => f(vals[Number(i)] ?? ""));
        const hl = (l: string) => put(inline(l, link, color), esc), pl = (l: string) => put(plainInline(l, link), (v) => v);
        for (const p of paragraphs(t)) {
          if (p.kind === "p") {
            html.push(`<tr><td style="padding:6px 32px;font-family:${FONT};font-size:16px;line-height:1.55;color:${INK}">${p.lines.map(hl).join("<br>")}</td></tr>`);
            text.push(p.lines.map(pl).join("\n"), "");
          } else {
            html.push(`<tr><td style="padding:2px 32px 6px;font-family:${FONT};font-size:16px;line-height:1.55;color:${INK}"><ul style="margin:0;padding-left:22px">${p.lines.map((l) => `<li style="margin:2px 0">${hl(l)}</li>`).join("")}</ul></td></tr>`);
            text.push(p.lines.map((l) => `- ${pl(l)}`).join("\n"), "");
          }
        }
        break;
      }
      case "button": {
        const label = m(b.label).trim(), url = link(b.url);
        if (!label || !url) break;
        const align = b.align === "left" ? "left" : "center";
        html.push(`<tr><td align="${align}" style="padding:14px 32px"><table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td bgcolor="${color}" style="border-radius:6px"><a href="${esc(url)}" style="display:inline-block;padding:12px 22px;font-family:${FONT};font-size:16px;font-weight:600;color:#ffffff;text-decoration:none;border-radius:6px">${esc(label)}</a></td></tr></table></td></tr>`);
        text.push(`${label}: ${url}`, "");
        break;
      }
      case "image": {
        const src = safeUrl(b.src); if (!/^https?:\/\//i.test(src)) break;
        const w = b.width === "half" ? 268 : 536;
        const img = `<img src="${esc(src)}" alt="${esc(m(b.alt ?? ""))}" width="${w}" style="display:block;width:100%;max-width:${w}px;height:auto;border:0;border-radius:4px;margin:0 auto">`;
        html.push(`<tr><td align="center" style="padding:10px 32px">${b.url ? `<a href="${esc(link(b.url))}">${img}</a>` : img}</td></tr>`);
        if (b.alt) text.push(`[${m(b.alt)}]${b.url ? ` ${link(b.url)}` : ""}`, "");
        break;
      }
      case "quote": {
        const t = m(b.text); if (!t.trim()) break;
        html.push(`<tr><td style="padding:10px 32px"><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="border-left:3px solid ${color};padding:4px 0 4px 16px;font-family:${FONT};font-size:17px;line-height:1.5;color:${INK};font-style:italic">${esc(t)}${b.by ? `<div style="font-style:normal;font-size:14px;color:${MUTED};margin-top:6px">${esc(m(b.by))}</div>` : ""}</td></tr></table></td></tr>`);
        text.push(`"${t}"${b.by ? `\n— ${m(b.by)}` : ""}`, "");
        break;
      }
      case "divider":
        html.push(`<tr><td style="padding:12px 32px"><div style="border-top:1px solid #E1E4E8;height:1px;line-height:1px;font-size:1px">&nbsp;</div></td></tr>`);
        text.push("---", "");
        break;
      case "spacer": {
        const h = b.size === "s" ? 8 : b.size === "l" ? 40 : 20;
        html.push(`<tr><td style="height:${h}px;line-height:${h}px;font-size:1px">&nbsp;</td></tr>`);
        break;
      }
      case "signature": {
        const name = vars.sender_name?.trim(), email = vars.sender_email?.trim();
        if (!name && !email) break;
        const org = input.brand?.companyName && input.brand.companyName !== name ? input.brand.companyName : null;
        html.push(`<tr><td style="padding:10px 32px;font-family:${FONT};font-size:15px;line-height:1.5;color:${INK}">${name ? `<strong>${esc(name)}</strong>` : ""}${org ? `<br><span style="color:${MUTED}">${esc(org)}</span>` : ""}${email ? `<br><a href="mailto:${esc(email)}" style="color:${color}">${esc(email)}</a>` : ""}</td></tr>`);
        text.push([name, org, email].filter(Boolean).join("\n"), "");
        break;
      }
    }
  }

  const f = input.footer ?? {};
  const foot: string[] = [];
  if (f.reason) foot.push(esc(m(f.reason)));
  if (f.text) foot.push(esc(m(f.text)));
  const company = input.brand?.companyName ? esc(input.brand.companyName) : "";
  if (company || f.postalAddress) foot.push([company, esc(f.postalAddress ?? "").replace(/\n/g, ", ")].filter(Boolean).join(" · "));
  const links: string[] = [];
  if (f.prefsUrl) links.push(`<a href="${esc(f.prefsUrl)}" style="color:${MUTED};text-decoration:underline">Choose what you get</a>`);
  if (f.unsubscribeUrl) links.push(`<a href="${esc(f.unsubscribeUrl)}" style="color:${MUTED};text-decoration:underline">Unsubscribe</a>`);
  if (links.length) foot.push(links.join(" &nbsp;·&nbsp; "));
  const plainFoot = [f.reason ? m(f.reason) : "", f.text ? m(f.text) : "", [input.brand?.companyName, (f.postalAddress ?? "").replace(/\n/g, ", ")].filter(Boolean).join(" · "),
    f.prefsUrl ? `Choose what you get: ${f.prefsUrl}` : "", f.unsubscribeUrl ? `Unsubscribe: ${f.unsubscribeUrl}` : ""].filter(Boolean);
  const footCell = `font-family:${FONT};font-size:12px;line-height:1.6;color:${MUTED};text-align:center`;
  const hidden = preheader.trim() ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;mso-hide:all">${esc(preheader)}${"&#8199;&#65279;&#847; ".repeat(40)}</div>` : "";
  const plainOf = (lines: string[]) => [...lines, "--", ...plainFoot].join("\n").replace(/\n{3,}/g, "\n\n").trim();

  // An imported design keeps its own look. Courage fills in the merge tags, tags the links, adds the preview text,
  // and puts the footer (why they're getting it, the postal address, unsubscribe) under it.
  if (design) {
    const body = tagLinks(merge(design.html, vars, esc), input.utm ?? null);
    if (!/<body\b/i.test(body)) {
      const rows = `<tr><td style="padding:24px 32px;font-family:${FONT};font-size:16px;line-height:1.55;color:${INK}">${body}</td></tr>`;
      return { subject, html: shell(subject, hidden, rows, foot, footCell), text: plainOf([htmlToText(body)]) };
    }
    // On a white box, so it reads on any background the design has.
    const footer = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td align="center" style="padding:16px 8px 24px">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#FFFFFF" style="max-width:600px;border-radius:6px"><tr><td style="padding:14px 24px;${footCell}">${foot.join("<br>")}</td></tr></table></td></tr></table>`;
    let doc = body;
    if (!/<meta[^>]+charset/i.test(doc)) doc = doc.replace(/<head\b[^>]*>/i, (h) => `${h}<meta charset="utf-8">`);
    doc = doc.replace(/<body\b[^>]*>/i, (b) => b + hidden);
    const end = doc.toLowerCase().lastIndexOf("</body");
    doc = end >= 0 ? doc.slice(0, end) + footer + doc.slice(end) : doc + footer;
    if (!/^\s*<!doctype/i.test(doc)) doc = `<!doctype html>\n${doc}`;
    return { subject, html: doc, text: plainOf([htmlToText(body)]) };
  }

  const logo = input.brand?.logoUrl && /^https?:\/\//i.test(input.brand.logoUrl)
    ? `<tr><td style="padding:24px 32px 8px"><img src="${esc(input.brand.logoUrl)}" alt="${company}" height="32" style="display:block;height:32px;width:auto;border:0"></td></tr>`
    : company ? `<tr><td style="padding:24px 32px 8px;font-family:${FONT};font-size:14px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:${color}">${company}</td></tr>` : `<tr><td style="height:20px;font-size:1px">&nbsp;</td></tr>`;
  const rows = `${logo}\n${html.join("\n")}\n<tr><td style="height:24px;font-size:1px">&nbsp;</td></tr>`;
  return { subject, html: shell(subject, hidden, rows, foot, footCell), text: plainOf(text) };
}

/** Courage's own layout: a white card on a light background, the footer under it. */
function shell(subject: string, hidden: string, rows: string, foot: string[], footCell: string): string {
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="x-apple-disable-message-reformatting"><title>${esc(subject)}</title>
<style>@media (max-width:620px){.card{width:100%!important;border-radius:0!important}.card td{padding-left:20px!important;padding-right:20px!important}}</style></head>
<body style="margin:0;padding:0;background:#F4F5F6">
${hidden}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="#F4F5F6"><tr><td align="center" style="padding:24px 8px">
<table role="presentation" class="card" width="600" cellpadding="0" cellspacing="0" border="0" bgcolor="#FFFFFF" style="width:600px;max-width:600px;border-radius:8px;border:1px solid #E1E4E8">
${rows}
</table>
<table role="presentation" class="card" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px"><tr><td style="padding:16px 32px;${footCell}">${foot.join("<br>")}</td></tr></table>
</td></tr></table></body></html>`;
}

// ---------------------------------------------------------------------------------------------------------------
// Imported HTML: cleaned on the way in, checked before sending, and turned into plain text for the text version.
// ---------------------------------------------------------------------------------------------------------------

const ENTITIES: Record<string, string> = {
  amp: "&", lt: "<", gt: ">", quot: '"', apos: "'", nbsp: " ", mdash: "—", ndash: "–", hellip: "…", rsquo: "’", lsquo: "‘", rdquo: "”", ldquo: "“",
  copy: "©", reg: "®", trade: "™", bull: "•", middot: "·", rarr: "→", larr: "←", zwnj: "", zwj: "", shy: "",
};
export function decodeEntities(s: string): string {
  return String(s ?? "").replace(/&(#\d+|#x[0-9a-f]+|[a-z]+);/gi, (whole, e: string) => {
    if (e[0] !== "#") return ENTITIES[e.toLowerCase()] ?? whole;
    const n = e[1] === "x" || e[1] === "X" ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
    return Number.isFinite(n) && n > 0 && n < 0x110000 ? String.fromCodePoint(n) : "";
  });
}

export type CleanedHtml = { html: string; removed: string[]; title: string | null };
const BAD_URL = String.raw`(?:javascript|vbscript|data:text\/html)`;
const URL_ATTRS = String.raw`(?:href|src|action|formaction|background|poster|xlink:href)`;

/** What inboxes refuse anyway, taken out so it doesn't hurt delivery: scripts and event handlers, script links,
 *  embedded pages, forms, redirects. Outlook's conditional comments and <style> stay. Also unwraps a pasted
 *  ```html code block. Previews in Courage run in a sandbox with no scripts, so this is about delivery, not trust. */
export function cleanHtml(raw: string): CleanedHtml {
  let h = String(raw ?? "").replace(/^﻿/, "").replace(/\r\n?/g, "\n").trim();
  const fence = h.match(/^```[\w-]*\n([\s\S]*?)\n?```$/);
  if (fence) h = fence[1].trim();
  const removed = new Map<string, number>();
  const count = (label: string) => removed.set(label, (removed.get(label) ?? 0) + 1);
  const drop = (label: string, re: RegExp) => { h = h.replace(re, () => { count(label); return ""; }); };
  drop("script", /<script\b[\s\S]*?<\/script\s*>/gi);
  drop("script", /<script\b[^>]*>/gi);
  drop("embedded page", /<(iframe|frameset|object|applet)\b[\s\S]*?<\/\1\s*>/gi);
  drop("embedded page", /<(iframe|frame|frameset|object|applet|embed)\b[^>]*>/gi);
  drop("form field", /<(textarea|select|button)\b[\s\S]*?<\/\1\s*>/gi);
  drop("form field", /<input\b[^>]*>/gi);
  h = h.replace(/<\/?form\b[^>]*>/gi, "");
  drop("redirect", /<meta\b[^>]*http-equiv\s*=\s*["']?refresh[^>]*>/gi);
  h = h.replace(/<base\b[^>]*>/gi, "");
  h = h.replace(/([\s"'/;])on[a-z]+\s*=/gi, (_, pre: string) => { count("script"); return `${pre}data-removed=`; });
  h = h.replace(new RegExp(String.raw`(\s${URL_ATTRS}\s*=\s*)(?:"\s*${BAD_URL}[^"]*"|'\s*${BAD_URL}[^']*'|${BAD_URL}[^\s>]*)`, "gi"), (_, pre: string) => { count("script link"); return `${pre}"#"`; });
  const t = h.match(/<title\b[^>]*>([\s\S]*?)<\/title\s*>/i);
  const title = t ? decodeEntities(t[1].replace(/<[^>]+>/g, "")).replace(/\s+/g, " ").trim().slice(0, 200) || null : null;
  return { html: h, removed: [...removed].map(([k, n]) => `${n} ${k}${n === 1 ? "" : "s"}`), title };
}

/** Web links in the design get the campaign's utm tags, like the builder's links do (Outlook's VML buttons too). */
function tagLinks(html: string, utm: Utm): string {
  if (!utm) return html;
  return html.replace(/(<(?:a|v:[a-z]+)\b[^>]*?\shref\s*=\s*)(?:"([^"]*)"|'([^']*)')/gi, (whole, pre: string, d?: string, s?: string) => {
    const url = decodeEntities(d ?? s ?? "").trim();
    return /^https?:\/\/[^\s{}]+$/i.test(url) ? `${pre}"${esc(tagLink(url, utm))}"` : whole;
  });
}

const hrefsOf = (html: string) => [...html.matchAll(/<(?:a|v:[a-z]+)\b[^>]*?\shref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi)].map((x) => decodeEntities(x[1] ?? x[2] ?? x[3] ?? "").trim());
const srcsOf = (html: string) => [...html.matchAll(/<img\b[^>]*?\ssrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/gi)].map((x) => decodeEntities(x[1] ?? x[2] ?? x[3] ?? "").trim());

/** The words of an HTML email, for its plain-text version: links as "label (url)", lists as "- ", no hidden text. */
export function htmlToText(html: string): string {
  let h = String(html ?? "").replace(/<!--[\s\S]*?-->/g, "").replace(/<(head|style|script|title)\b[\s\S]*?<\/\1\s*>/gi, "");
  h = h.replace(/<(div|span|p|td|table)\b[^>]*display\s*:\s*none[^>]*>[\s\S]*?<\/\1\s*>/gi, "");
  h = h.replace(/<a\b[^>]*?\shref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))[^>]*>([\s\S]*?)<\/a\s*>/gi, (_, d?: string, s?: string, u?: string, inner = "") => {
    const url = decodeEntities(d ?? s ?? u ?? "").trim();
    const label = inner.replace(/<img\b[^>]*?\salt\s*=\s*(?:"([^"]*)"|'([^']*)')[^>]*>/gi, " $1$2 ").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (!/^https?:\/\//i.test(url)) return label;
    return !label || decodeEntities(label) === url ? ` ${url} ` : `${label} (${url})`;
  });
  h = h.replace(/<br\s*\/?>/gi, "\n").replace(/<li\b[^>]*>/gi, "\n- ").replace(/<\/t[dh]\s*>/gi, " ")
    .replace(/<\/?(p|div|h[1-6]|tr|table|ul|ol|blockquote|section|article|header|footer|center)\b[^>]*>/gi, "\n");
  return decodeEntities(h.replace(/<[^>]+>/g, ""))
    .replace(/[​-‍ ͏﻿­]/g, "")
    .split("\n").map((l) => l.replace(/[ \t ]+/g, " ").trim()).join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

const PLACEHOLDER = /(^|[./])(example\.(com|org|net)|your(company|domain|site|website)\.com)(\/|$|[?#])/i;

/** What would go wrong if an imported design went out as it is. These hold the email back, like the builder's checks. */
function designProblems(html: string): string[] {
  const out: string[] = [];
  const hrefs = hrefsOf(html);
  if (hrefs.some((u) => !u || u === "#" || /^https?:\/*$/i.test(u))) out.push("Some links go nowhere (# or blank): give every button and link its address");
  if (hrefs.some((u) => u && u !== "#" && !/^(https?:|mailto:|tel:|#|\{\{)/i.test(u))) out.push("Links need a full address that starts with https://");
  if (hrefs.some((u) => PLACEHOLDER.test(u)) || srcsOf(html).some((u) => PLACEHOLDER.test(u))) out.push("Replace the example links (like example.com) with yours");
  const srcs = srcsOf(html);
  if (srcs.some((u) => /^http:\/\//i.test(u))) out.push("Images need an https:// address");
  if (srcs.some((u) => !/^https?:\/\//i.test(u))) out.push("Every image needs an https:// web address, not a file or an image saved inside the HTML");
  if (/\*\|[A-Z0-9_:]+\|\*/.test(html) || /\{\{(?!\s*[a-z_]+\s*(?:\|[^}]*)?\}\})/i.test(html)) out.push("Some merge tags aren't Courage's (like *|FNAME|*): use {{first_name|there}} and the others in the list");
  return out;
}

/** Worth knowing about an imported design, but it can still go out. */
export function warnings(e: { blocks: Block[] }): string[] {
  const d = designOf(e.blocks);
  if (!d) return [];
  const out: string[] = [];
  const kb = Math.round(new TextEncoder().encode(d.html).length / 1024);
  if (kb > 90) out.push(`It's ${kb} KB. Gmail cuts off emails over about 100 KB behind a "View entire message" link, so make it lighter`);
  if (/display\s*:\s*(inline-)?(flex|grid)|position\s*:\s*(absolute|fixed)/i.test(d.html)) out.push("It uses flex, grid or positioned layout, which Outlook ignores, so columns may stack or overlap. Ask for a table-based layout");
  if (/<link\b[^>]*rel\s*=\s*["']?stylesheet/i.test(d.html) && !/<link\b[^>]*fonts\.googleapis/i.test(d.html)) out.push("Styles in a linked stylesheet don't load in Gmail or Outlook. Ask for inline styles");
  if (/<svg\b|\.svg(\?|"|')/i.test(d.html)) out.push("Gmail and Outlook don't show SVG images. Use PNG or JPG");
  if (/unsubscribe/i.test(htmlToText(d.html))) out.push("It has its own unsubscribe text. Courage adds a working unsubscribe link under it, so you can take yours out");
  return out;
}

/** Rough check before sending: a subject, something in the body, and links that look like links. */
export function problems(e: { subject: string; blocks: Block[] }): string[] {
  const out: string[] = [];
  if (!e.subject?.trim()) out.push("Give it a subject line");
  const design = designOf(e.blocks);
  if (design) {
    if (!htmlToText(design.html) && !/<img\b/i.test(design.html)) out.push("Add some content");
    out.push(...designProblems(design.html));
    const bad = unknownTags(e.subject, design.html);
    if (bad.length) out.push(`Unknown merge tag${bad.length > 1 ? "s" : ""}: ${bad.map((t) => `{{${t}}}`).join(", ")}`);
    return [...new Set(out)];
  }
  if (!(e.blocks ?? []).some((b) => (b.type === "text" && b.text.trim()) || (b.type === "heading" && b.text.trim()) || (b.type === "image" && b.src))) out.push("Add some content");
  for (const b of e.blocks ?? []) {
    if (b.type === "button" && (!b.label?.trim() || !b.url?.trim() || /^https?:\/*$/i.test(b.url.trim()))) out.push("A button needs a label and a link");
    if ((b.type === "button" || b.type === "image") && /yourcompany\.com/i.test(("url" in b ? b.url : "") ?? "")) out.push("Replace the example link (yourcompany.com) with yours");
    if (b.type === "image" && b.src && !/^https:\/\//i.test(b.src.trim())) out.push("Images need an https:// address");
  }
  const bad = unknownTags(e.subject, ...(e.blocks ?? []).flatMap((b) => ("text" in b ? [b.text] : []).concat("label" in b ? [b.label] : [])));
  if (bad.length) out.push(`Unknown merge tag${bad.length > 1 ? "s" : ""}: ${bad.map((t) => `{{${t}}}`).join(", ")}`);
  return [...new Set(out)];
}

/** Blocks for a new email, so the composer never opens on a blank page. */
export function starterBlocks(kind: "newsletter" | "announcement" | "event" | "plain" = "announcement"): Block[] {
  const id = () => Math.random().toString(36).slice(2, 10);
  if (kind === "plain") return [
    { id: id(), type: "text", text: "Hi {{first_name|there}},\n\n" },
    { id: id(), type: "signature" },
  ];
  if (kind === "event") return [
    { id: id(), type: "heading", text: "Join us at booth 4411" },
    { id: id(), type: "text", text: "Hi {{first_name|there}},\n\nWe'll be at the show next week with the new 5-axis cell running live parts. Stop by and bring a print: our engineers will quote it on the spot." },
    { id: id(), type: "button", label: "Book a time with our team", url: "https://" },
    { id: id(), type: "signature" },
  ];
  if (kind === "newsletter") return [
    { id: id(), type: "heading", text: "What's new this month" },
    { id: id(), type: "text", text: "Hi {{first_name|there}},\n\nHere's what we've been working on." },
    { id: id(), type: "divider" },
    { id: id(), type: "heading", text: "Customer story", size: "medium" },
    { id: id(), type: "text", text: "How one customer cut scrap by 30% on a high-mix line." },
    { id: id(), type: "button", label: "Read the story", url: "https://", align: "left" },
    { id: id(), type: "divider" },
    { id: id(), type: "heading", text: "From the shop floor", size: "medium" },
    { id: id(), type: "text", text: "- New capacity\n- Shorter lead times\n- An open house on the calendar" },
    { id: id(), type: "signature" },
  ];
  return [
    { id: id(), type: "heading", text: "A headline that says what's new" },
    { id: id(), type: "text", text: "Hi {{first_name|there}},\n\nOne or two short paragraphs: what it is, why it matters to {{company|your team}}, and what to do next." },
    { id: id(), type: "button", label: "Learn more", url: "https://" },
    { id: id(), type: "signature" },
  ];
}
